// /onboarding/shape — shape 4, «ماذا يحجز الزبون؟» (board 25 v2 shape--4):
// the unit types. Each row: «{name}», «{quantity words} · {min nights words}
// على الأقل», «{price} د.ع / ليلة|يوم». «إضافة نوع وحدة» opens an empty
// «نوع الوحدة» panel; a row opens it filled (PATCH). The weekend sub comes
// from the business's scheduling `weekend_nights`. Finish needs one active
// type (R8; W12 until then).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { iqd } from '@/lib/format';
import { useLayout } from '@/lib/layout';
import { countLabel } from '@/lib/terminology';
import { Msg } from '../../components/widgets';
import { Wizard } from '../../components/Wizard';
import { SHAPE_COMMON, SHAPE_COPY, UNIT } from '../../copy';
import { finishShape } from '../../data/enroll';
import { getWeekendNights, listUnitTypes, type UnitType } from '../../data/shapeApi';
import { useBusinessPreview } from '../Business';
import { UnitTypePanel } from './UnitTypePanel';

/** «وحدة واحدة · ليلة واحدة على الأقل» */
export function unitTypeSub(t: Pick<UnitType, 'quantity' | 'min_nights' | 'price_unit'>): string {
  return `${countLabel(t.quantity, UNIT.units)} · ${countLabel(t.min_nights, t.price_unit === 'day' ? UNIT.days : UNIT.nights)} ${UNIT.atLeast}`;
}

export function UnitTypesStep({ businessId }: { businessId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const mobile = useLayout() === 'phone';
  const key = ['onboarding', 'unit-types', businessId];
  const types = useQuery({ queryKey: key, queryFn: () => listUnitTypes(businessId), staleTime: Infinity, retry: 0 });
  const weekend = useQuery({ queryKey: ['onboarding', 'weekend', businessId], queryFn: () => getWeekendNights(businessId), staleTime: Infinity, retry: 0 });
  const [panel, setPanel] = useState<{ editing: UnitType | null } | null>(null);

  const list = types.data ?? [];
  const { profile, preview } = useBusinessPreview('shape');
  const hasActive = list.some((t) => t.active);
  const loading = types.isPending;

  const saved = (t: UnitType) =>
    qc.setQueryData<UnitType[]>(key, (xs) => {
      const cur = xs ?? [];
      return cur.some((x) => x.id === t.id) ? cur.map((x) => (x.id === t.id ? t : x)) : [...cur, t];
    });

  return (
    <Wizard
      screen="shape"
      testId={loading ? 'onb-shape-loading' : 'onb-shape-units'}
      state={loading ? 'loading' : 'default'}
      list
      question={SHAPE_COPY.unit.question()}
      sub={SHAPE_COPY.unit.sub()}
      primary={
        // while the panel is open its «حفظ» is the screen's one coral
        panel ? undefined : (
          <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} disabled={!hasActive}>
            {SHAPE_COMMON.finish}
          </PrimaryAction>
        )
      }
      onSubmit={() => {
        if (hasActive && !panel) finishShape(navigate);
      }}
      error={types.isError ? { kind: 'network', onRetry: () => void types.refetch() } : null}
      panel={{ phase: 'preview', profile, preview }}
    >
      {loading ? (
        <Skeleton height={120} />
      ) : (
        <>
          <div className="onb-listcard" data-testid="onb-unit-types">
            {list.map((t) => (
              <button key={t.id} type="button" className="onb-row onb-ut-row" data-testid="onb-ut-row" onClick={() => setPanel({ editing: t })}>
                <div className="onb-row-text">
                  <div className="onb-row-title">{t.name}</div>
                  <div className="onb-row-sub">{unitTypeSub(t)}</div>
                </div>
                <span className="onb-ut-price">
                  {iqd(t.price_iqd)} / {UNIT.per(t.price_unit)}
                </span>
              </button>
            ))}
            <button type="button" className="onb-entity-add" onClick={() => setPanel({ editing: null })}>
              <Plus aria-hidden="true" />
              {UNIT.add}
            </button>
          </div>
          <Msg tone="info" testId="onb-unit-note">
            {UNIT.addSub}
          </Msg>
          {!hasActive && (
            <Msg tone="info" testId="onb-shape-min-one">
              {SHAPE_COMMON.atLeastOne}
            </Msg>
          )}
        </>
      )}
      {panel && <UnitTypePanel businessId={businessId} editing={panel.editing} weekendNights={weekend.data ?? [4, 5]} onClose={() => setPanel(null)} onSaved={saved} />}
    </Wizard>
  );
}
